const theory = `
<h3 class=likesectionHead>Introduction</h3>
<p>In this lesson, we present a conceptual overview about the uncertainties and the limitations of the state-of-the-art methods applied in acoustic simulation. The origins of uncertainties of these computer simulation methods will be discussed. The methods to obtain quantitative data on uncertainties can be based on intercomparisons (referred to as “round robins”).<br class=newline>
<p>
   The commonly used room-acoustic simulation algorithms are based on geometrical acoustics (GA) as standard models for the prediction of room-acoustic characteristics, acoustic design and consulting. The validity of simulation results depends on many factors, such as 3D surface modelling and the boundary conditions (scattering and absorption) which are used as input data. The inaccuracies of input data lead to uncertainties in room acoustic parameter prediction, such as reverberation time, clarity, early decay time, etc. The theory of error propagation of uncertainties can be used to predict the inaccuracies in these parameters and it was shown that reverberation times, T, prediction with accuracy better than the just noticeable difference (JND) of about 5
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>%</mi>
   </math>
   of T, accurate absorption coefficients are required. However, absorption data with this level of accuracy is usually not available in databases <span class=cite>[<span class=cmbx-10>5</span>]</span>. Quantitative uncertainties in almost all the acoustic prediction and simulation tools have not yet been studied in detail. The users of these tools cannot make sure that they will obtain correct results when they use some published material data and individually created computer-aided design models of the room shape.<br class=newline>
<p>There might be multiple sources of such uncertainties including the assumptions in room acoustics modelling techniques and algorithm. For example, wave effects are neglected in GA. The description of sound fields is based on energy decays and the direction of particles or rays incident on the receiver. This approach is correct as long as the relevant dimensions of the room geometry are large compared with wavelengths and broad band signals are taken into account. These approximations are usually valid and with accuracy enough in large rooms intended for speech and music far above the Schroeder frequency. Distinct wave effects, however, such as the seat dip effect or diffraction at columns cannot be simulated with GA approaches. In these cases, full wave-based models should be chosen. The finite element method (FEM), the boundary element method (BEM), and the method of finite differences in time domain (FDTD) are good candidates, for which, however, the same requirements on accuracy of input data apply.<br class=newline>
<p>For analysis of uncertainties, powerful tools can be used which are based on analysis of uncertainties of measurements. The principles of the ISO guide for expression of uncertainty in measurement (GUM) <span class=cite>[<span class=cmbx-10>1</span>]</span> have not yet been widely considered for acoustics.
<h3 class=likesectionHead>Systematic uncertainties</h3>
<p>Especially simulation models based on GA are based on various assumptions and simplifications, e.g., not considering diffracted sound. Therefore their results lead to deviations caused by shortcomings of the model.
<h4 class=likesubsectionHead>Level of details in room models</h4>
<p>The surface elements of a rooms are usually reduced to a set of polygons which must be large compared with wavelengths. This is required covering three decades in order to cover the audio frequency range, which is practically impossible. The results may be wrong due to a physically unfortunate choice of the level of detail. A high level of detail that is too high will lead to unnecessary long computation times and to wrong reflection phenomena, when rays are hitting a geometric fine structure which does not create a specular reflection. A typical example for this effect when using ray tracing is modelling of staircases, which are rather a ramp with scattering than a series of discrete steps. Also, a large potential is identified in the acceleration of algorithms at low frequencies at low spatial resolution in the polygon model, and at late times in the impulse response, where the late decay is built by scattering rather than by deterministic specular reflections in a detailed polygon model.
<h4 class=likesubsectionHead>Curved surfaces</h4>
<p>
   Curved surfaces produce very special features like focal points. Important points to consider are whether an approximation by planes produces a focus and whether the sound level in the focal region and the size of the focal region are correct <span class=cite>[<span class=cmbx-10>3</span>]</span>. For a hemispherical dome, for example, the energy is distributed over a circular area with a width of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>
   . For small wavelengths, the focusing effect is therefore quite strong. Generally, a reduction of the extremely high pressure by absorbers or diffusers in the curved boundary is not sufficient to eliminate the focusing effect. Outside the focal, a strong interfering sound field is observed. Computer models based on image source methods are not capable of describing the focal pressure, and wave models are the only solution to the problem.
<h4 class=likesubsectionHead>Spherical wave impedances</h4>
<p>The ray reflection model used in ray tracing and image source algorithms is a correct solution of the wave equation for rigid boundaries. For the well-known problem of a point source above an impedance plane as one non-rigid boundary, it can be shown that the spherical wave solution based on complex impedance is a good approximation if the position of source and receiver are not located too close to the wall. In flat rooms or in long rooms like corridors, grazing incidence angles may occur quite often so that the plane wave reflection is too rough an approximation at low frequencies. Therefore, where room modes are to be calculated only wave-based models can be used, such as BEM or FEM or similar approaches.
<h3 class=likesectionHead>Stochastic uncertainties</h3>
<p>Stochastic uncertainties in GA simulations acoustics are usually caused by the configuration and implementation of the simulation algorithm and by the boundary conditions of absorption and scattering, which are often integrated in databases included in the simulation tool. Software parameters such as the particle count impact the computation time and are thus of interest if compromises between accuracy and effort must be found <span class=cite>[<span class=cmbx-10>5</span>]</span>.
<h4 class=likesubsectionHead>Stochastic ray tracing</h4>
<p>Ray tracing algorithms which are used for estimation of reverberation tails typically involve stochastic processes and thus are non-deterministic algorithms. Such stochastic processes, for example, are required to account for scattered reflections. The decision if a particle is being scattered is based on a random number process <span class=cite>[<span class=cmbx-10>4</span>]</span>, depending of the scattering coefficient of the corresponding wall.
<p>
   The standard deviation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>σ</mi>
         </mrow>
         <mrow>
            <mstyle class=text>
               <mtext>EH</mtext>
            </mstyle>
         </mrow>
      </msub>
   </math>
   of an energy histogram (with time bin size
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Δt</mi>
   </math>
   ) obtained with the ray tracing model using
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   particles, a detection sphere with a radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   is theoretically described by Eq. 1 <span class=cite>[<span class=cmbx-10>4</span>]</span>,
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>σ</mi>
               </mrow>
               <mrow>
                  <mstyle class=text>
                     <mtext>IR</mtext>
                  </mstyle>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>4</mn>
            <mn>34</mn>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                        <mi>π</mi>
                        <msubsup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mi>c</mi>
                        <mi mathvariant=italic>Δt</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   being the volume of the simulated room and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   the speed of sound. This equation describes the standard deviation of an energy histogram or a room impulse response obtained energy multiplication-based ray tracing. This stochastic uncertainty for varying ray tracing parameters and different particle counts is presented interactively in the principle part of this lesson. Next to the stochastic error of the impulse response, also the stochastic error related to the calculation of integral room parameters (e.g., the strength
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>G</mi>
   </math>
   ), are shown. Here, the standard deviation is calculated according to Eq. 2,
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>σ</mi>
               </mrow>
               <mrow>
                  <mstyle class=text>
                     <mtext>G</mtext>
                  </mstyle>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>4</mn>
            <mn>34</mn>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                        <mn>8</mn>
                        <mi>π</mi>
                        <msubsup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   being the equivalent absorption area of the simulated room. For such parameters, the stochastic uncertainty is substantially lower as the temporal structure of the energy is less relevant for the result.<br class=newline>
<h4 class=likesubsectionHead>Boundary conditions</h4>
<p>In addition to an uncertainty which is introduced during the measurement or modeling procedures for the boundary conditions (for GA models: absorption and scattering data), the selection and application of the boundary condition data by the operator represents another source of uncertainty, which needs to be considered.<br class=newline>Another source of uncertainty relates to the type of input data. Absorption and scattering coefficients in GA simulations are mostly used as random-incidence coefficient, but also angle-dependent data could be applied and would, in theory, lead to more accurate simulation results. Obtaining such datasets, however, is challenging and thus angle-dependent data is hardly available. The question is whether the uncertainty due to using random-incidence data is sufficiently small to obtain acceptable results of room acoustic simulations. This depends on the simulated scene, if the sound field can be considered as diffuse and uniform sound incidence can be assumed, random incidence coefficients are likely to be sufficient.<br class=newline>Surface scattering is often also differently modelled in simulation tools. Comparisons using different software implementations show that the results reflect the same trends when the scattering coefficients are changed, but the absolute results differ although the same input data was used <span class=cite>[<span class=cmbx-10>2</span>]</span>. Thus, the uncertainty due to the chosen scattering model and data is fairly high, but is less relevant than the uncertainty of the selected absorption coefficients, as this data is the main factor influencing the acoustic decay process of a room.
<p>
<h3 class=likesectionHead>Glossary</h3>
<div class=supertabular>
<dl>
  <dt>BEM</dt>
  <dd>Boundary Element Method</dd>
  <dt>FDTD</dt>
  <dd>Finite Differences in Time Domain</dd>
  <dt>FEM</dt>
  <dd>Finite Element Mthod</dd>
  <dt>GA</dt>
  <dd>Geometrical Acoustics</dd>
  <dt>GUM</dt>
  <dd>Guide for expression of Uncertainty in Measurement</dd>
  <dt>JND</dt>
  <dd>Just Noticeable Difference</dd>
</dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography>[1]</dt>
   <dd
        <p>ISO/IEC-Guide-98-3. Uncertainty of measurement — part 3: Guide to the expression of uncertainty in measurement, 2008.</p>
   </dd>
      <dt class=thebibliography>[2]</dt>
   <dd>
     <p>L. Shtrepi, A. Astolfi, S. Pelzer, R. Vitale, and M. Rycht ́arikov ́a. Objective and perceptual assessment of the scattered sound field in a simulated concert hall. J. Acoust. Soc. of Am., 138(3):1485–1497, 2015.</p>
   </dd>
   <dt class=thebibliography>[3]</dt>
    <dd>
      <p>
        M. Vercammen. Improving the accuracy of sound absorption measurement according to iso 354. In Proc. Int. Symp. on Room Acoustics (ISRA), Melbourne, Australia, 2010.
      </p>
    </dd>
    <dt class=thebibliography[4]</dt>
    <dd>
        <p>M. Vorl ̈ander. Auralization. Fundamentals of acoustics, modelling, simulation, algorithms and acoustic virtual reality. Springer, Berlin, Heidelberg, Germany, 1st edition, 2008</p>
    </dd>
    <dt>[5]</dt>
    <dd>
        <p>
            M. Vorl ̈ander. Computer simulations in room acoustics: Concepts and uncertainties. J. Acoust. Soc. Am., 133(3):1203–1213, Mar. 2013.
        </p>
    </dd>
</dl>
`;

export default theory;
